import type { DeutscheBahnOfferPrice, DeutscheBahnPassenger } from "../types";

/** Passenger categories of the offer (`offer_prices`). Entries without an `external_reference` are dropped. */
export function getDeutscheBahnOfferPrices(offer: any): DeutscheBahnOfferPrice[] {
  const offerPrices = offer?.offer_prices;

  if (!Array.isArray(offerPrices)) {
    return [];
  }

  return offerPrices.filter(
    (offerPrice: any) => typeof offerPrice?.external_reference === "string" && offerPrice.external_reference !== ""
  );
}

/** First category in API order (Adult), or "" when the offer has none. */
export function getDefaultDeutscheBahnPassengerType(offerPrices: DeutscheBahnOfferPrice[]): string {
  return offerPrices[0]?.external_reference ?? "";
}

/** Label for a passenger type: the matching `description`, or the raw code when it is not offered. */
export function getDeutscheBahnOfferPriceLabel(
  offerPrices: DeutscheBahnOfferPrice[],
  passengerType: string | undefined
): string {
  if (!passengerType) {
    return "";
  }

  return offerPrices.find(offerPrice => offerPrice.external_reference === passengerType)?.description || passengerType;
}

/**
 * Gives the default type to every passenger whose type is not offered (e.g. the empty type seeded before
 * the info GET). Offered types are kept, so a refetch after a language change keeps the guest's choices.
 * Returns the same list when nothing changes.
 */
export function syncDeutscheBahnPassengerTypes(
  passengerList: DeutscheBahnPassenger[],
  offerPrices: DeutscheBahnOfferPrice[]
): DeutscheBahnPassenger[] {
  const list = Array.isArray(passengerList) ? passengerList : [];
  const defaultType = getDefaultDeutscheBahnPassengerType(offerPrices);
  const isOffered = (passengerType: string) =>
    offerPrices.some(offerPrice => offerPrice.external_reference === passengerType);

  if (!defaultType || list.every(passenger => isOffered(passenger.passenger_type))) {
    return list;
  }

  return list.map(passenger =>
    isOffered(passenger.passenger_type) ? passenger : { ...passenger, passenger_type: defaultType }
  );
}

/**
 * Empty passenger row. The first row is seeded before the info GET with an empty type;
 * `syncDeutscheBahnPassengerTypes` sets the default once `offer_prices` load.
 */
export const createInitialDeutscheBahnPassenger = (passengerType = "", hasRemove = false): DeutscheBahnPassenger => ({
  first_name: "",
  last_name: "",
  passenger_type: passengerType,
  hasRemove
});
